import { useEffect } from 'react';

/**
 * Reliably focuses an input field after component mount
 * 
 * More reliable than autoFocus prop because:
 * - Waits for DOM to be fully ready
 * - Works after programmatic navigation
 * - Handles animations/async content
 * - Respects loading states
 * 
 * @param {string} inputId - The ID of the input element to focus
 * @param {boolean} isLoading - Optional loading state to prevent focus during submission
 * @param {number} delay - Optional delay in ms (default: 300ms for animations to settle)
 * 
 * @example
 * // In your component:
 * const [isLoading, setIsLoading] = useState(false);
 * useAutoFocus('email', isLoading);
 */
export function useAutoFocus(inputId, isLoading = false, delay = 300) {
  useEffect(() => {
    // Wait for DOM to be fully ready and any animations to settle
    const focusTimer = setTimeout(() => {
      const input = document.getElementById(inputId);
      if (input && !isLoading) {
        input.focus();
      }
    }, delay);

    return () => clearTimeout(focusTimer);
  }, []); // Only run once on mount
}

export default useAutoFocus;

